'use client';

import { useMemo } from 'react';
import { X, Check } from 'lucide-react';
import { METRICS, CATEGORY_LABELS, type MetricId, type MetricCategory } from '../lib/metric-definitions';

interface Props {
  open: boolean;
  onClose: () => void;
  onAdd: (id: MetricId) => void;
  activeMetrics: MetricId[];
}

export function MetricPicker({ open, onClose, onAdd, activeMetrics }: Props) {
  const grouped = useMemo(() => {
    const g: Record<string, MetricId[]> = {};
    for (const id of Object.keys(METRICS) as MetricId[]) {
      const m = METRICS[id];
      if (!g[m.category]) g[m.category] = [];
      g[m.category]!.push(id);
    }
    return g;
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-[6px] p-10"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-neutral-900 rounded-2xl max-w-[560px] w-full max-h-[80vh] overflow-hidden flex flex-col shadow-[0_20px_60px_rgba(0,0,0,0.25)] animate-in fade-in zoom-in-95 duration-200"
      >
        <div className="px-6 py-5 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h3
            className="m-0 font-semibold tracking-[-0.01em] text-[1.25rem]"
          >
            Adaugă <em style={{ color: 'rgb(var(--primary-500))', fontStyle: 'italic', fontWeight: 500 }}>metrică</em>
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 grid place-items-center hover:bg-neutral-200 dark:hover:bg-neutral-700 transition"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 pb-5 pt-1 flex-1">
          {(Object.entries(grouped) as unknown as [MetricCategory, MetricId[]][]).map(([cat, ids]) => (
            <div key={cat}>
              <div className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-neutral-500 dark:text-neutral-400 mt-4 mb-2 flex items-center gap-2.5">
                <span>{CATEGORY_LABELS[cat]}</span>
                <span className="flex-1 h-px bg-neutral-200 dark:bg-neutral-800" />
              </div>
              <div className="flex flex-col gap-1">
                {ids.map((id) => {
                  const m = METRICS[id];
                  const added = activeMetrics.includes(id);
                  return (
                    <button
                      key={id}
                      onClick={() => { if (!added) onAdd(id); onClose(); }}
                      disabled={added}
                      className={
                        'flex items-center gap-2.5 px-2.5 py-2 border border-transparent rounded-lg text-left font-inherit transition-all ' +
                        (added
                          ? 'opacity-60 cursor-not-allowed'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:border-neutral-200 dark:hover:border-neutral-700 cursor-pointer')
                      }
                    >
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: m.color }} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[0.8125rem] font-medium text-neutral-900 dark:text-neutral-100">{m.label}</span>
                        <span className="block text-[0.6875rem] text-neutral-500 dark:text-neutral-400 mt-0.5">{m.description}</span>
                      </span>
                      {added && (
                        <span className="inline-flex items-center gap-1 text-[0.625rem] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                          <Check className="h-3 w-3" /> adăugat
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
